/**
 * web/src/pages/Payroll.tsx
 * 給与管理ページ（管理者専用）
 *
 * タブ:
 *   1. 月次給与一覧 — 一覧・編集・プレビュー・PDF印刷
 *   2. 税務設定     — アシスタント別年度別税務情報
 *   3. 源泉所得税集計 — 月別納付管理＋アシスタント別年間集計
 *   4. 統合月次一覧 — 通常アシスタント（payroll_records）とヘルプスタッフ
 *                    （one_time_work_entries）を同一月次一覧で確認できる、読み取り専用の統合ビュー
 */

import { useEffect, useState, useCallback, useMemo } from 'react'
import { api } from '../api/client'
import type { MonthlyPayee } from '../api/client'
import { ConfirmDialog, Toast } from '../components/ConfirmDialog'
import { PayslipPreview, type PayrollRecord } from '../components/PayslipPreview'
import { HelpPayslipPreview } from '../components/HelpPayslipPreview'
import { BonusSlipPreview, type BonusRecord } from '../components/BonusSlipPreview'
import { BonusEditModal, BONUS_STATUS_LABELS, type BonusEditRecord } from '../components/BonusEditModal'

// ================================================================
// ヘルパー
// ================================================================
function fmt(n: number | null | undefined) { return (n ?? 0).toLocaleString('ja-JP') }
function fmtOrDash(n: number | null | undefined) {
  if (n == null || n === 0) return '—'
  return n.toLocaleString('ja-JP')
}

// issued_at は D1 の UTC datetime や ISO文字列が混ざる可能性があるため、一覧ではJST日付で表示する。
function fmtDateJst(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  const raw = String(dateStr).trim()

  const dateOnly = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (dateOnly) {
    return `${dateOnly[1]}-${dateOnly[2].padStart(2, '0')}-${dateOnly[3].padStart(2, '0')}`
  }

  const d1 = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?$/)
  const d = d1
    ? new Date(`${d1[1]}-${d1[2].padStart(2, '0')}-${d1[3].padStart(2, '0')}T${d1[4].padStart(2, '0')}:${d1[5]}:${(d1[6] ?? '00').padStart(2, '0')}Z`)
    : new Date(raw)

  if (Number.isNaN(d.getTime())) return '—'

  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d)
  const get = (type: string) => parts.find(part => part.type === type)?.value ?? ''
  const y = get('year')
  const m = get('month')
  const day = get('day')
  return y && m && day ? `${y}-${m}-${day}` : '—'
}

const STATUS_LABELS: Record<string, string> = {
  draft: '下書き', confirmed: '確定', paid: '支払済', canceled: 'キャンセル',
}
const STATUS_BADGE: Record<string, string> = {
  draft: 'badge-gray', confirmed: 'badge-blue', paid: 'badge-green', canceled: 'badge-red',
}
const TAX_COL_LABELS: Record<string, string> = { ko: '甲欄', otsu: '乙欄', hei: '丙欄' }

// 統合月次一覧（combined タブ）用ステータスラベル。
// help側の status（pending/draft/submitted/confirmed）は通常アシスタントの
// payroll_records.status（draft/confirmed/paid/canceled）と語彙が異なる（'draft'の意味が違う）ため、
// source_type別に分けて解決する。バッジの色（強調/非強調）は is_settled のみで判定し既存ロジックのまま。
const HELP_PAYEE_STATUS_LABELS: Record<string, string> = {
  pending: '未提出', draft: '一時保存', submitted: '提出済み', confirmed: '確認済み',
}
function payeeStatusLabel(p: MonthlyPayee): string {
  if (p.source_type === 'help') return HELP_PAYEE_STATUS_LABELS[p.status] ?? p.status
  return STATUS_LABELS[p.status] ?? p.status
}

// payroll_month は「働いた月」。支払日は翌月10日をデフォルトにする。
// 例: 2026-04 → 2026-05-10
function defaultPayDateForPayrollMonth(month: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(month)
  if (!m) return ''

  const y = Number(m[1])
  const mon = Number(m[2])
  const nextMonth = mon === 12 ? 1 : mon + 1
  const nextYear = mon === 12 ? y + 1 : y

  return `${nextYear}-${String(nextMonth).padStart(2, '0')}-10`
}

// 税務警告を生成
function taxWarnings(record: PayrollRecord, taxProfile: any): string[] {
  const warns: string[] = []
  if (!taxProfile) { warns.push('税務設定が未登録です'); return warns }
  if (!taxProfile.has_fuyou_form) warns.push('扶養控除等申告書が未提出のため、乙欄扱いになります')
  if (taxProfile.tax_column === 'otsu' && record.income_tax === 0) {
    warns.push('所得税が0円ですが、税区分が乙欄の場合は確認してください')
  }
  const thisYear = new Date().getFullYear()
  if (taxProfile.tax_year < thisYear) {
    warns.push(`前年（${taxProfile.tax_year}年）の税務設定しかありません。今年分を確認してください`)
  }
  return warns
}

// ================================================================
// 給与明細編集モーダル
// ================================================================
type EditRecord = Partial<PayrollRecord> & { assistant_id: string; payroll_month: string }

// paid/confirmed から「戻す」変更かどうか（フロント側の判定。バックエンドの
// isPayrollStatusRevert と同じロジックを保つこと）。
function isStatusRevert(fromStatus: string, toStatus: string): boolean {
  if (fromStatus === toStatus) return false
  if (fromStatus === 'paid') return true
  if (fromStatus === 'confirmed' && toStatus === 'draft') return true
  return false
}

function PayrollEditModal({
  initial, assistants, onClose, onSaved,
}: {
  initial: EditRecord | null
  assistants: any[]
  onClose: () => void
  onSaved: () => void
}) {
  const isNew = !initial?.id
  const baseline = {
    assistant_id: '', payroll_month: '', pay_date: '', issuer_name: 'スタジオねこのみや／浪川周',
    basic_pay: 0, overtime_pay: 0, position_allowance: 0, qualification_allowance: 0,
    housing_allowance: 0, family_allowance: 0, commuting_allowance: 0, reimbursed_transportation: 0,
    health_insurance: 0, pension: 0, employment_insurance: 0, income_tax: 0, resident_tax: 0,
    property_savings: 0, life_insurance: 0, reserve_fund: 0, union_fee: 0, other_deductions: 0,
    year_end_refund: 0, year_end_collection: 0, other_adjustment: 0,
    work_days: 0, absent_days: 0, paid_leave_days: 0, paid_leave_remaining: 0,
    work_minutes: 0, overtime_minutes: 0,
    remarks: '', status: 'draft',
    ...initial,
  }
  const [form, setForm] = useState<Record<string, any>>(() => ({ ...baseline }))
  const [saving, setSaving] = useState(false)
  const [err,    setErr]    = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [confirmRevert, setConfirmRevert] = useState(false)

  const initialStatus = baseline.status
  const reverting = !isNew && isStatusRevert(initialStatus, form.status)

  // 新規作成時は、選択中の対象月から支払日（翌月10日）を自動補完する。
  useEffect(() => {
    if (!isNew) return
    if (!form.payroll_month) return

    const nextPayDate = defaultPayDateForPayrollMonth(form.payroll_month)
    if (!nextPayDate) return

    setForm((f: Record<string, any>) => {
      if (f.pay_date) return f
      return { ...f, pay_date: nextPayDate }
    })
  }, [isNew, form.payroll_month, form.pay_date])

  const n = (k: string) => Number(form[k] ?? 0)
  const set = (k: string, v: unknown) => setForm((f: Record<string, any>) => ({ ...f, [k]: v }))

  // 自動計算（プレビュー）
  const taxable    = n('basic_pay') + n('overtime_pay') + n('position_allowance') +
                     n('qualification_allowance') + n('housing_allowance') + n('family_allowance')
  const nonTaxable = n('commuting_allowance') + n('reimbursed_transportation')
  const gross      = taxable + nonTaxable
  const deductions = n('health_insurance') + n('pension') + n('employment_insurance') +
                     n('income_tax') + n('resident_tax') + n('property_savings') +
                     n('life_insurance') + n('reserve_fund') + n('union_fee') + n('other_deductions')
  const adjTotal   = n('year_end_refund') - n('year_end_collection') + n('other_adjustment')
  const netPay     = gross - deductions + adjTotal

  const fullPayload = {
    ...form,
    taxable_pay_total: taxable, non_taxable_pay_total: nonTaxable,
    gross_pay_total: gross, deductions_total: deductions,
    adjustment_total: adjTotal, net_pay: netPay,
  }

  // 既存レコードの編集では、初期値から実際に変わったフィールドだけを送る。
  // （支払済みロックが、値の変わらないフィールドのキーが含まれているだけで
  //   誤って反応しないようにするため。ステータスだけ変更した場合は status のみ送られる）
  const diffPayload = () => {
    const diff: Record<string, any> = {}
    for (const [k, v] of Object.entries(fullPayload)) {
      const b = (baseline as Record<string, any>)[k]
      const changed = typeof v === 'number' || typeof b === 'number'
        ? Number(v) !== Number(b ?? 0)
        : v !== b
      if (changed) diff[k] = v
    }
    return diff
  }

  const doSave = async () => {
    setSaving(true)
    setErr(null)
    try {
      if (isNew) {
        await api.payroll.create(fullPayload)
      } else {
        const payload = diffPayload()
        if (reverting) payload.status_change_reason = reason.trim()
        await api.payroll.update(initial!.id!, payload)
      }
      onSaved()
      onClose()
    } catch (e: any) {
      setErr(e.message ?? '保存に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async () => {
    if (!form.assistant_id || !form.payroll_month) {
      setErr('アシスタントと対象月は必須です')
      return
    }
    if (reverting && !reason.trim()) {
      setErr('ステータスを戻す理由の入力が必須です')
      return
    }
    setErr(null)
    if (reverting) {
      setConfirmRevert(true)
      return
    }
    await doSave()
  }

  const AmountField = ({ label, k }: { label: string; k: string }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <label style={{ fontSize: 10, color: 'var(--ink-3)', fontWeight: 600 }}>{label}</label>
      <input type="number" value={form[k] ?? 0} min={0}
        onChange={e => set(k, Number(e.target.value))}
        style={{ fontFamily: 'var(--font-mono)', textAlign: 'right', fontSize: 13 }}
      />
    </div>
  )

  const SectionTitle = ({ label }: { label: string }) => (
    <div style={{ gridColumn: '1/-1', fontWeight: 700, fontSize: 11, color: 'var(--ink-2)',
      textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid var(--paper-2)',
      paddingBottom: 4, marginTop: 8 }}>
      {label}
    </div>
  )

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 700, maxHeight: '92vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <div className="modal-title">{isNew ? '給与明細 新規作成' : '給与明細 編集'}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 12, padding: '8px 12px', background: 'var(--red-light)', borderRadius: 6 }}>{err}</div>}

          {/* 基本情報 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 12 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>アシスタント *</label>
              <select value={form.assistant_id} onChange={e => set('assistant_id', e.target.value)} disabled={!isNew}>
                <option value="">選択してください</option>
                {assistants.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>対象月 *</label>
              <input type="month" value={form.payroll_month} onChange={e => {
                const nextMonth = e.target.value
                const oldDefaultPayDate = defaultPayDateForPayrollMonth(form.payroll_month)
                setForm((f: Record<string, any>) => ({
                  ...f,
                  payroll_month: nextMonth,
                  pay_date: !f.pay_date || f.pay_date === oldDefaultPayDate
                    ? defaultPayDateForPayrollMonth(nextMonth)
                    : f.pay_date,
                }))
              }} disabled={!isNew} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>支払日</label>
              <input type="date" value={form.pay_date ?? ''} onChange={e => set('pay_date', e.target.value)} />
            </div>
          </div>

          {/* 金額グリッド */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
            <SectionTitle label="支給" />
            <AmountField label="基本給" k="basic_pay" />
            <AmountField label="時間外手当" k="overtime_pay" />
            <AmountField label="役職手当" k="position_allowance" />
            <AmountField label="資格手当" k="qualification_allowance" />
            <AmountField label="住居手当" k="housing_allowance" />
            <AmountField label="家族手当" k="family_allowance" />
            <AmountField label="通勤手当" k="commuting_allowance" />
            <AmountField label="立替交通費" k="reimbursed_transportation" />

            {/* 自動計算プレビュー */}
            <div style={{ gridColumn: '1/-1', display: 'flex', gap: 16, padding: '8px 12px',
              background: 'var(--paper-2)', borderRadius: 6, fontSize: 12 }}>
              <span>課税支給合計：<strong>¥{fmt(taxable)}</strong></span>
              <span>非課税支給合計：<strong>¥{fmt(nonTaxable)}</strong></span>
              <span>総支給額：<strong style={{ color: 'var(--green)' }}>¥{fmt(gross)}</strong></span>
            </div>

            <SectionTitle label="控除" />
            <AmountField label="健康保険" k="health_insurance" />
            <AmountField label="厚生年金" k="pension" />
            <AmountField label="雇用保険" k="employment_insurance" />
            <AmountField label="所得税" k="income_tax" />
            <AmountField label="住民税" k="resident_tax" />
            <AmountField label="財形貯蓄" k="property_savings" />
            <AmountField label="生命保険料" k="life_insurance" />
            <AmountField label="積立金" k="reserve_fund" />
            <AmountField label="組合費" k="union_fee" />
            <AmountField label="その他控除" k="other_deductions" />
            <div style={{ gridColumn: '1/-1', display: 'flex', gap: 16, padding: '8px 12px',
              background: 'var(--paper-2)', borderRadius: 6, fontSize: 12 }}>
              <span>控除合計：<strong>¥{fmt(deductions)}</strong></span>
            </div>

            <SectionTitle label="その他調整" />
            <AmountField label="年末調整還付" k="year_end_refund" />
            <AmountField label="年末調整徴収" k="year_end_collection" />
            <AmountField label="その他調整" k="other_adjustment" />

            <SectionTitle label="勤怠" />
            {[
              ['出勤日数', 'work_days', '0.5'], ['欠勤日数', 'absent_days', '0.5'],
              ['有給日数', 'paid_leave_days', '0.5'], ['有給残日数', 'paid_leave_remaining', '0.5'],
            ].map(([label, k, step]) => (
              <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <label style={{ fontSize: 10, color: 'var(--ink-3)', fontWeight: 600 }}>{label}</label>
                <input type="number" step={step} value={form[k] ?? 0} min={0}
                  onChange={e => set(k, Number(e.target.value))}
                  style={{ textAlign: 'right', fontSize: 13 }} />
              </div>
            ))}
            <div style={{ gridColumn: '1/-1', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginTop: 4 }}>
              {[
                ['勤務時間（分）', 'work_minutes'],
                ['時間外労働時間（分）', 'overtime_minutes'],
              ].map(([label, k]) => (
                <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <label style={{ fontSize: 10, color: 'var(--ink-3)', fontWeight: 600 }}>{label}</label>
                  <input type="number" step="1" value={form[k] ?? 0} min={0}
                    onChange={e => set(k, Number(e.target.value))}
                    style={{ textAlign: 'right', fontSize: 13 }} />
                </div>
              ))}
              <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 6, fontSize: 10, color: 'var(--ink-3)' }}>
                ※ インポート時に勤怠データから自動取得
              </div>
            </div>
          </div>

          {/* 差引支給額プレビュー */}
          <div style={{ marginTop: 12, padding: '12px 16px', background: 'var(--green-light)',
            border: '1px solid rgba(45,122,58,0.25)', borderRadius: 8,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>差引支給額（自動計算）</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 700, color: 'var(--green)' }}>
              ¥{fmt(netPay)}
            </span>
          </div>

          {/* 備考・発行者・ステータス */}
          <div style={{ marginTop: 12 }}>
            <div className="form-group">
              <label>備考</label>
              <textarea rows={3} value={form.remarks ?? ''}
                onChange={e => set('remarks', e.target.value)}
                style={{ resize: 'vertical', fontSize: 12 }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label>発行者</label>
                <input type="text" value={form.issuer_name ?? ''} onChange={e => set('issuer_name', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>ステータス</label>
                <select value={form.status} onChange={e => set('status', e.target.value)}>
                  {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>

            {reverting && (
              <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--red-light)',
                border: '1px solid rgba(200,60,60,0.25)', borderRadius: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--red)', fontWeight: 600, marginBottom: 6 }}>
                  「{STATUS_LABELS[initialStatus] ?? initialStatus}」から「{STATUS_LABELS[form.status] ?? form.status}」に戻します。
                  {initialStatus === 'paid' && ' 支払額・源泉徴収額・明細内容に影響する可能性があります。'}
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>変更理由 *</label>
                  <textarea rows={2} value={reason} onChange={e => setReason(e.target.value)}
                    placeholder="例: 交通費の入力誤りが見つかったため再編集します"
                    style={{ resize: 'vertical', fontSize: 12 }} />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>キャンセル</button>
          <button className={`btn ${reverting ? 'btn-danger' : 'btn-primary'}`} onClick={handleSave} disabled={saving}>
            {saving ? '保存中...' : reverting ? 'ステータスを戻して保存' : '保存'}
          </button>
        </div>
      </div>
      {confirmRevert && (
        <ConfirmDialog
          title="ステータスを戻します"
          message={
            `${STATUS_LABELS[initialStatus] ?? initialStatus} から ${STATUS_LABELS[form.status] ?? form.status} に戻します。\n` +
            (initialStatus === 'paid' ? '支払額・源泉徴収額・明細への影響にご注意ください。\n' : '') +
            `理由: ${reason.trim()}\n\nよろしいですか？`
          }
          confirmLabel="ステータスを戻す"
          confirmDanger
          onConfirm={async () => { setConfirmRevert(false); await doSave() }}
          onClose={() => setConfirmRevert(false)}
        />
      )}
    </div>
  )
}

// ================================================================
// 税務設定 編集モーダル
// ================================================================
function TaxProfileEditModal({
  initial, assistants, onClose, onSaved,
}: {
  initial: any | null; assistants: any[]; onClose: () => void; onSaved: () => void
}) {
  const [form, setForm] = useState<Record<string, any>>({
    assistant_id: '', tax_year: new Date().getFullYear(),
    tax_column: 'ko', has_fuyou_form: 1, dependents_count: 0,
    year_end_adjustment_enabled: 1, withholding_slip_required: 1,
    submitted_on: '', notes: '',
    ...initial,
  })
  const [saving, setSaving] = useState(false)
  const set = (k: string, v: unknown) => setForm((f: Record<string, any>) => ({ ...f, [k]: v }))

  const handleSave = async () => {
    setSaving(true)
    try {
      if (initial?.id) {
        await api.taxProfiles.update(initial.id, form)
      } else {
        await api.taxProfiles.upsert(form)
      }
      onSaved(); onClose()
    } finally { setSaving(false) }
  }

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <div className="modal-title">税務設定</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>アシスタント</label>
              <select value={form.assistant_id} onChange={e => set('assistant_id', e.target.value)} disabled={!!initial?.id}>
                <option value="">選択</option>
                {assistants.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>年度</label>
              <input type="number" value={form.tax_year} onChange={e => set('tax_year', Number(e.target.value))} min={2020} disabled={!!initial?.id} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>税区分</label>
              <select value={form.tax_column} onChange={e => set('tax_column', e.target.value)}>
                {Object.entries(TAX_COL_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>扶養親族等の人数</label>
              <input type="number" value={form.dependents_count} min={0} onChange={e => set('dependents_count', Number(e.target.value))} />
            </div>
          </div>

          {[
            ['has_fuyou_form', '扶養控除等申告書', '提出済み', '未提出'],
            ['year_end_adjustment_enabled', '年末調整', '対象', '対象外'],
            ['withholding_slip_required', '源泉徴収票発行', '対象', '対象外'],
          ].map(([k, label, yes, no]) => (
            <div key={k} className="form-group" style={{ margin: '10px 0 0' }}>
              <label>{label}</label>
              <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                {[[1, yes], [0, no]].map(([val, lbl]) => (
                  <label key={String(val)} style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer', fontSize: 13 }}>
                    <input type="radio" checked={form[k] === val} onChange={() => set(k, val)} />
                    {lbl}
                  </label>
                ))}
              </div>
            </div>
          ))}

          <div className="form-group" style={{ marginTop: 10 }}>
            <label>申告書提出日</label>
            <input type="date" value={form.submitted_on ?? ''} onChange={e => set('submitted_on', e.target.value)} />
          </div>
          <div className="form-group">
            <label>メモ</label>
            <textarea rows={2} value={form.notes ?? ''} onChange={e => set('notes', e.target.value)} style={{ resize: 'vertical' }} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>キャンセル</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? '...' : '保存'}</button>
        </div>
      </div>
    </div>
  )
}

// ================================================================
// メインページ
// ================================================================
type Tab = 'list' | 'tax' | 'withholding' | 'combined' | 'bonuses'

export default function Payroll() {
  const thisYear  = new Date(Date.now() + 9 * 60 * 60 * 1000).getFullYear()
  const thisMonth = new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 7)

  const [tab, setTab]               = useState<Tab>('list')
  const [month, setMonth]           = useState(thisMonth)
  const [year, setYear]             = useState(thisYear)
  const [records, setRecords]       = useState<PayrollRecord[]>([])
  const [taxProfiles, setTaxProfiles] = useState<any[]>([])
  const [withholding, setWithholding] = useState<any>(null)
  const [assistantWithholding, setAssistantWithholding] = useState<any>(null)
  const [assistants, setAssistants]  = useState<any[]>([])
  const [loading, setLoading]        = useState(false)
  const [toast, setToast]            = useState<{ ok: boolean; msg: string } | null>(null)
  const [editRecord, setEditRecord]  = useState<EditRecord | null>(null)
  const [showEdit, setShowEdit]      = useState(false)
  const [previewRecord, setPreviewRecord] = useState<PayrollRecord | null>(null)
  const [editTaxProfile, setEditTaxProfile] = useState<any>(null)
  const [showTaxEdit, setShowTaxEdit] = useState(false)
  const [importLoading, setImportLoading] = useState(false)
  const [bulkStatus, setBulkStatus] = useState<'draft' | 'confirmed' | 'paid' | 'canceled'>('confirmed')
  const [bulkStatusLoading, setBulkStatusLoading] = useState(false)
  const [confirmOp, setConfirmOp]    = useState<{ type: string; id: string; name: string } | null>(null)
  const [bulkRevertConfirm, setBulkRevertConfirm] = useState(false)
  const [payees, setPayees]          = useState<MonthlyPayee[]>([])
  const [payeesLoading, setPayeesLoading] = useState(false)
  const [helpPreview, setHelpPreview] = useState<{ id: string; name: string } | null>(null)
  const [bonusRecords, setBonusRecords] = useState<BonusRecord[]>([])
  const [bonusLoading, setBonusLoading] = useState(false)
  const [editBonusRecord, setEditBonusRecord] = useState<BonusEditRecord | null>(null)
  const [showBonusEdit, setShowBonusEdit] = useState(false)
  const [previewBonusRecord, setPreviewBonusRecord] = useState<BonusRecord | null>(null)
  const [confirmBonusOp, setConfirmBonusOp] = useState<{ id: string; name: string } | null>(null)

  const showToast = (ok: boolean, msg: string) => { setToast({ ok, msg }); setTimeout(() => setToast(null), 5000) }

  // 給与レコード・税務設定の人選択ドロップダウン用。退職予定・退職済みでも
  // 給与対象（payroll_enabled=1）なら選択できるようにする（最終給与の作成・税区分設定のため）。
  const loadAssistantsForPicker = async () => {
    const assts = await api.assistants.list({ status: 'all' })
    return (assts as any[]).filter((a) => a?.payroll_enabled !== 0)
  }

  const loadRecords = useCallback(async () => {
    setLoading(true)
    try {
      const [recs, assts] = await Promise.all([
        api.payroll.list(month),
        loadAssistantsForPicker(),
      ])
      setRecords(recs)
      setAssistants(assts)
    } finally { setLoading(false) }
  }, [month])

  const loadTax = useCallback(async () => {
    const [profiles, assts] = await Promise.all([
      api.taxProfiles.list(year),
      loadAssistantsForPicker(),
    ])
    setTaxProfiles(profiles)
    setAssistants(assts)
  }, [year])

  const loadWithholding = useCallback(async () => {
    const [summary, assistantSummary] = await Promise.all([
      api.withholding.summary(year),
      api.withholding.assistants(year),
    ])
    setWithholding(summary)
    setAssistantWithholding(assistantSummary)
  }, [year])

  const loadPayees = useCallback(async () => {
    setPayeesLoading(true)
    try {
      const res = await api.monthlyPayees.list(month)
      setPayees(res.payees)
    } finally { setPayeesLoading(false) }
  }, [month])

  const loadBonuses = useCallback(async () => {
    setBonusLoading(true)
    try {
      const [recs, assts] = await Promise.all([
        api.bonuses.list({ year }),
        loadAssistantsForPicker(),
      ])
      setBonusRecords(recs)
      setAssistants(assts)
    } finally { setBonusLoading(false) }
  }, [year])

  useEffect(() => { if (tab === 'list')        loadRecords() },    [tab, loadRecords])
  useEffect(() => { if (tab === 'tax')         loadTax() },        [tab, loadTax])
  useEffect(() => { if (tab === 'withholding') loadWithholding() }, [tab, loadWithholding])
  useEffect(() => { if (tab === 'combined')    loadPayees() },     [tab, loadPayees])
  useEffect(() => { if (tab === 'bonuses')     loadBonuses() },    [tab, loadBonuses])

  const handleImport = async () => {
    setImportLoading(true)
    try {
      const res = await api.payroll.importFromMonthlyPayment(month)
      const skippedParts = [
        res.skippedConfirmed > 0 ? `確定済み${res.skippedConfirmed}件` : null,
        res.skippedPaid > 0      ? `支払済み${res.skippedPaid}件`     : null,
        res.skippedOther > 0     ? `その他${res.skippedOther}件`      : null,
      ].filter(Boolean).join('、')
      showToast(true,
        `新規作成: ${res.inserted}件 / 更新: ${res.updated}件` +
        (skippedParts ? ` / スキップ（変更なし）: ${skippedParts}` : '')
      )
      loadRecords()
    } catch (e: any) {
      showToast(false, e.message ?? 'インポートに失敗しました')
    } finally { setImportLoading(false) }
  }

  const [bulkReason, setBulkReason] = useState('')

  const bulkTargets = () => records.filter(r => r.status !== 'canceled')
  const bulkHasReverting = () => bulkTargets().some(r => isStatusRevert(r.status, bulkStatus))

  const runBulkStatus = async (reason?: string) => {
    const targets = bulkTargets()
    setBulkStatusLoading(true)
    try {
      const results = await Promise.allSettled(
        targets.map(r => api.payroll.update(r.id, {
          status: bulkStatus,
          ...(isStatusRevert(r.status, bulkStatus) ? { status_change_reason: reason } : {}),
        }))
      )
      const failed = results.filter(r => r.status === 'rejected') as PromiseRejectedResult[]
      const succeeded = results.length - failed.length
      const label = STATUS_LABELS[bulkStatus] ?? bulkStatus
      if (failed.length === 0) {
        showToast(true, `一括変更しました: ${succeeded}件 → ${label}`)
      } else {
        const firstErr = failed[0]?.reason?.message ?? '不明なエラー'
        showToast(false, `一部失敗しました: 成功${succeeded}件 / 失敗${failed.length}件（${firstErr}）`)
      }
    } finally {
      setBulkStatusLoading(false)
      loadRecords()
    }
  }

  const handleBulkStatus = async () => {
    const targets = bulkTargets()
    if (targets.length === 0) {
      showToast(false, '一括変更できる給与明細がありません')
      return
    }

    const label = STATUS_LABELS[bulkStatus] ?? bulkStatus
    if (bulkHasReverting()) {
      setBulkReason('')
      setBulkRevertConfirm(true)
      return
    }

    if (!window.confirm(`${month} の給与明細 ${targets.length}件をすべて「${label}」に変更します。よろしいですか？`)) return
    await runBulkStatus()
  }

  const handleCancel = async (id: string) => {
    try {
      await api.payroll.cancel(id)
      showToast(true, 'キャンセルしました')
      loadRecords()
    } catch (e: any) { showToast(false, e.message) }
  }

  const handleLogIssue = async (id: string) => {
    try { await api.payroll.logIssue(id, 'pdf') } catch { /* ignore */ }
    loadRecords()
  }

  const handleBonusCancel = async (id: string) => {
    try {
      await api.bonuses.cancel(id)
      showToast(true, 'キャンセルしました')
      loadBonuses()
    } catch (e: any) { showToast(false, e.message) }
  }

  const handleBonusLogIssue = async (id: string) => {
    try { await api.bonuses.logIssue(id, 'pdf') } catch { /* ignore */ }
  }

  // 統合月次一覧から賞与明細を開く（bonus_record_id から実データを取得）
  const openBonusPreviewFromPayee = async (bonusRecordId: string) => {
    try {
      const record = await api.bonuses.get(bonusRecordId)
      setPreviewBonusRecord(record)
    } catch (e: any) {
      showToast(false, e.message ?? '賞与明細の取得に失敗しました')
    }
  }

  const handleAssistantWithholdingCsv = () => {
    const rows = assistantWithholding?.assistants ?? []
    if (rows.length === 0) {
      showToast(false, '出力できるアシスタント別集計がありません')
      return
    }

    const csvCell = (v: unknown) => {
      const text = v == null ? '' : String(v)
      return `"${text.replace(/"/g, '""')}"`
    }
    const taxLabel = (v: string | null | undefined) => v ? (TAX_COL_LABELS[v] ?? v) : '未設定'
    const yesNo = (v: unknown, yes = '対象', no = '対象外') => Number(v ?? 0) ? yes : no

    const header = [
      '年度','氏名','税区分','扶養控除等申告書','扶養親族数','年末調整','源泉徴収票',
      '支払回数','未確定/未支払件数','課税支給額合計','非課税支給額合計','総支給額',
      '社会保険料等合計','源泉所得税合計','控除合計','差引支給額合計','確認事項',
    ]
    const lines = [header.map(csvCell).join(',')]
    for (const r of rows) {
      lines.push([
        year,
        r.assistant_name,
        taxLabel(r.tax_column),
        r.tax_profile_id ? yesNo(r.has_fuyou_form, '提出済', '未提出') : '未設定',
        r.dependents_count ?? '',
        r.tax_profile_id ? yesNo(r.year_end_adjustment_enabled) : '未設定',
        r.tax_profile_id ? yesNo(r.withholding_slip_required) : '未設定',
        r.payment_count ?? 0,
        r.unfinalized_count ?? 0,
        r.taxable_pay_total ?? 0,
        r.non_taxable_pay_total ?? 0,
        r.gross_pay_total ?? 0,
        r.social_insurance_total ?? 0,
        r.income_tax_total ?? 0,
        r.deductions_total ?? 0,
        r.net_pay_total ?? 0,
        (r.warnings ?? []).join(' / '),
      ].map(csvCell).join(','))
    }

    const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `源泉徴収_アシスタント別年間集計_${year}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  // 税務プロファイルマップ（assistant_id → profile）
  const taxProfileMap = useMemo(() => {
    const map = new Map<string, any>()
    for (const tp of taxProfiles) {
      if (!map.has(tp.assistant_id) || tp.tax_year > map.get(tp.assistant_id).tax_year) {
        map.set(tp.assistant_id, tp)
      }
    }
    return map
  }, [taxProfiles])

  const TAB_LABELS: Record<Tab, string> = {
    list: '月次給与一覧', tax: '税務設定', withholding: '源泉所得税集計', combined: '統合月次一覧',
    bonuses: '賞与一覧',
  }

  return (
    <div>
      {toast && <Toast msg={toast.msg} ok={toast.ok} onClose={() => setToast(null)} />}

      {/* ConfirmDialog */}
      {confirmOp?.type === 'cancel' && (
        <ConfirmDialog
          title="給与明細をキャンセル"
          message={`${confirmOp.name} の給与明細をキャンセルします。\nステータスが「キャンセル」に変わります。よろしいですか？`}
          confirmLabel="キャンセルする"
          confirmDanger
          onConfirm={async () => { await handleCancel(confirmOp.id); setConfirmOp(null) }}
          onClose={() => setConfirmOp(null)}
        />
      )}

      {/* 一括ステータス変更：paid/confirmed から戻る場合は理由必須の確認ダイアログ */}
      {bulkRevertConfirm && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setBulkRevertConfirm(false)}>
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <div className="modal-title">ステータスを一括で戻します</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setBulkRevertConfirm(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: 'var(--red)', lineHeight: 1.7, whiteSpace: 'pre-wrap', marginBottom: 12 }}>
                {month} の給与明細 {bulkTargets().length}件を「{STATUS_LABELS[bulkStatus] ?? bulkStatus}」に一括変更します。
                このうち支払済み・確定済みのレコードが含まれる場合、それらは「戻す」変更になります。
                支払額・源泉徴収額・明細内容に影響する可能性があります。
              </p>
              <div className="form-group" style={{ margin: 0 }}>
                <label>変更理由（戻す対象のレコードに記録されます）*</label>
                <textarea rows={3} value={bulkReason} onChange={e => setBulkReason(e.target.value)}
                  placeholder="例: 7月分に金額の誤りが見つかったため一括で再編集します"
                  style={{ resize: 'vertical', fontSize: 12 }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setBulkRevertConfirm(false)}>キャンセル</button>
              <button className="btn btn-danger" disabled={!bulkReason.trim() || bulkStatusLoading}
                onClick={async () => { setBulkRevertConfirm(false); await runBulkStatus(bulkReason.trim()) }}>
                {bulkStatusLoading ? '変更中...' : '理由を記録して一括変更する'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 編集モーダル */}
      {showEdit && (
        <PayrollEditModal
          initial={editRecord}
          assistants={assistants}
          onClose={() => { setShowEdit(false); setEditRecord(null) }}
          onSaved={() => { loadRecords(); showToast(true, '保存しました') }}
        />
      )}

      {/* プレビューモーダル */}
      {previewRecord && (
        <PayslipPreview
          record={previewRecord}
          onClose={() => setPreviewRecord(null)}
          onLogIssue={() => handleLogIssue(previewRecord.id)}
        />
      )}

      {/* ヘルプ給与明細プレビュー */}
      {helpPreview && (
        <HelpPayslipPreview
          helpStaffId={helpPreview.id}
          helpStaffName={helpPreview.name}
          month={month}
          onClose={() => setHelpPreview(null)}
        />
      )}

      {/* 税務設定モーダル */}
      {showTaxEdit && (
        <TaxProfileEditModal
          initial={editTaxProfile}
          assistants={assistants}
          onClose={() => { setShowTaxEdit(false); setEditTaxProfile(null) }}
          onSaved={() => { loadTax(); showToast(true, '税務設定を保存しました') }}
        />
      )}

      {/* 賞与 編集モーダル */}
      {showBonusEdit && (
        <BonusEditModal
          initial={editBonusRecord}
          assistants={assistants}
          onClose={() => { setShowBonusEdit(false); setEditBonusRecord(null) }}
          onSaved={() => { loadBonuses(); showToast(true, '賞与を保存しました') }}
        />
      )}

      {/* 賞与明細プレビュー */}
      {previewBonusRecord && (
        <BonusSlipPreview
          record={previewBonusRecord}
          onClose={() => setPreviewBonusRecord(null)}
          onLogIssue={() => handleBonusLogIssue(previewBonusRecord.id)}
        />
      )}

      {/* 賞与キャンセル確認 */}
      {confirmBonusOp && (
        <ConfirmDialog
          title="賞与をキャンセル"
          message={`${confirmBonusOp.name} の賞与をキャンセルします。\nステータスが「キャンセル」に変わります。よろしいですか？`}
          confirmLabel="キャンセルする"
          confirmDanger
          onConfirm={async () => { await handleBonusCancel(confirmBonusOp.id); setConfirmBonusOp(null) }}
          onClose={() => setConfirmBonusOp(null)}
        />
      )}

      {/* タブナビ */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {(Object.keys(TAB_LABELS) as Tab[]).map(t => (
          <button key={t} className={`btn btn-sm ${tab === t ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setTab(t)}>
            {TAB_LABELS[t]}
          </button>
        ))}

        {/* 月 / 年 セレクター */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {(tab === 'list' || tab === 'combined') && (
            <input type="month" value={month} onChange={e => setMonth(e.target.value)}
              style={{ fontFamily: 'var(--font-mono)', fontSize: 13, width: 150 }} />
          )}
          {(tab === 'tax' || tab === 'withholding' || tab === 'bonuses') && (
            <select value={year} onChange={e => setYear(Number(e.target.value))}
              style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>
              {Array.from({ length: 5 }, (_, i) => thisYear - 2 + i).map(y => (
                <option key={y} value={y}>{y}年</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* ─────────────────────────────────────── */}
      {/* TAB 1: 月次給与一覧                    */}
      {/* ─────────────────────────────────────── */}
      {tab === 'list' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <button className="btn btn-outline btn-sm" onClick={() => {
              setEditRecord({ assistant_id: '', payroll_month: month, pay_date: defaultPayDateForPayrollMonth(month) } as any)
              setShowEdit(true)
            }}>+ 新規作成</button>
            <button className="btn btn-outline btn-sm" onClick={handleImport} disabled={importLoading}
              title="monthly_payments テーブルからこの月のデータをインポートします（未確定分は新規作成/更新。確定済み・支払済みは変更しません）">
              {importLoading ? '...' : '⬆ 月次集計からインポート'}
            </button>

            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 8 }}>
              <select
                value={bulkStatus}
                onChange={e => setBulkStatus(e.target.value as 'draft' | 'confirmed' | 'paid' | 'canceled')}
                style={{ height: 30, fontSize: 12 }}
                title="この月の給与明細ステータスを一括変更します"
              >
                {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <button
                className="btn btn-outline btn-sm"
                onClick={handleBulkStatus}
                disabled={bulkStatusLoading || records.length === 0}
                title="キャンセル以外の給与明細を、選択したステータスへ一括変更します"
              >
                {bulkStatusLoading ? '変更中...' : '全員ステータス変更'}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading" style={{ padding: 40 }}>LOADING...</div>
          ) : records.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-3)' }}>
              給与明細がありません。「月次集計からインポート」か「新規作成」で追加してください。
            </div>
          ) : (
            <div className="card" style={{ overflow: 'hidden' }}>
              <div className="table-wrap" style={{ overflowX: 'auto', width: '100%' }}>
                <table style={{ width: '100%', minWidth: 900 }}>
                  <colgroup>
                    <col style={{ width: '22%' }} />
                    <col style={{ width: 96 }} />
                    <col style={{ width: 118 }} />
                    <col style={{ width: 110 }} />
                    <col style={{ width: 90 }} />
                    <col style={{ width: 126 }} />
                    <col style={{ width: 106 }} />
                    <col style={{ width: 180 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>氏名</th>
                      <th>状態</th>
                      <th style={{ textAlign: 'right' }}>総支給額</th>
                      <th style={{ textAlign: 'right' }}>控除合計</th>
                      <th style={{ textAlign: 'right' }}>所得税</th>
                      <th style={{ textAlign: 'right' }}>差引支給額</th>
                      <th>発行日</th>
                      <th style={{ textAlign: 'right' }}>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map(r => {
                      const taxProfile = taxProfileMap.get(r.assistant_id)
                      const warns = taxWarnings(r, taxProfile)
                      return (
                        <tr key={r.id} style={{
                          opacity: r.status === 'canceled' ? 0.45 : 1,
                          background: warns.length > 0 ? 'rgba(176,122,18,0.04)' : undefined,
                        }}>
                          <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                            {r.assistant_name}
                            {warns.length > 0 && (
                              <span title={warns.join('\n')} style={{
                                marginLeft: 6, fontSize: 11, color: 'var(--amber)', cursor: 'help',
                              }}>⚠ 要確認</span>
                            )}
                          </td>
                          <td>
                            <span
                              className={`badge ${STATUS_BADGE[r.status] ?? 'badge-gray'}`}
                              style={{ display: 'inline-block', minWidth: 64, textAlign: 'center' }}
                            >
                              {STATUS_LABELS[r.status] ?? r.status}
                            </span>
                          </td>
                          <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(r.gross_pay_total)}</td>
                          <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(r.deductions_total)}</td>
                          <td className="mono" style={{ textAlign: 'right' }}>{fmtOrDash(r.income_tax)}</td>
                          <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>¥{fmt(r.net_pay)}</td>
                          <td className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                            {fmtDateJst(r.issued_at)}
                          </td>
                          <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                            <button className="btn btn-sm btn-ghost"
                              onClick={() => setPreviewRecord(r)}
                              style={{ fontSize: 11 }}>プレビュー</button>
                            <button className="btn btn-sm btn-outline"
                              onClick={() => { setEditRecord(r as any); setShowEdit(true) }}
                              style={{ fontSize: 11, marginLeft: 4 }}>編集</button>
                            {r.status !== 'canceled' && (
                              <button className="btn btn-sm btn-ghost"
                                style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 4 }}
                                onClick={() => setConfirmOp({ type: 'cancel', id: r.id, name: r.assistant_name })}>
                                ✕
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                    <tr style={{ background: 'var(--paper-2)', fontWeight: 700 }}>
                      <td>合計（{records.filter(r => r.status !== 'canceled').length}名）</td>
                      <td></td>
                      <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(records.filter(r => r.status !== 'canceled').reduce((s, r) => s + r.gross_pay_total, 0))}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(records.filter(r => r.status !== 'canceled').reduce((s, r) => s + r.deductions_total, 0))}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(records.filter(r => r.status !== 'canceled').reduce((s, r) => s + r.income_tax, 0))}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(records.filter(r => r.status !== 'canceled').reduce((s, r) => s + r.net_pay, 0))}</td>
                      <td colSpan={2}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────── */}
      {/* TAB 2: 税務設定                         */}
      {/* ─────────────────────────────────────── */}
      {tab === 'tax' && (
        <div>
          <div style={{ marginBottom: 12 }}>
            <button className="btn btn-outline btn-sm" onClick={() => { setEditTaxProfile(null); setShowTaxEdit(true) }}>
              + 税務設定を追加
            </button>
          </div>
          <div className="card">
            <div className="card-header"><span className="card-title">税務設定 — {year}年</span></div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>氏名</th><th>税区分</th><th>扶養控除申告書</th>
                    <th>扶養親族数</th><th>年末調整</th><th>源泉徴収票</th>
                    <th>申告書提出日</th><th>メモ</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {taxProfiles.length === 0 && (
                    <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: 'var(--ink-3)' }}>設定なし</td></tr>
                  )}
                  {taxProfiles.map(tp => (
                    <tr key={tp.id}>
                      <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{tp.assistant_name}</td>
                      <td><span className={`badge ${tp.tax_column === 'ko' ? 'badge-blue' : 'badge-amber'}`}>{TAX_COL_LABELS[tp.tax_column]}</span></td>
                      <td>{tp.has_fuyou_form ? <span className="badge badge-green">提出済</span> : <span className="badge badge-red">未提出</span>}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>{tp.dependents_count}名</td>
                      <td>{tp.year_end_adjustment_enabled ? '対象' : <span style={{ color: 'var(--ink-3)' }}>対象外</span>}</td>
                      <td>{tp.withholding_slip_required ? '対象' : <span style={{ color: 'var(--ink-3)' }}>対象外</span>}</td>
                      <td className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{tp.submitted_on ?? '—'}</td>
                      <td style={{ fontSize: 11, color: 'var(--ink-3)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tp.notes ?? '—'}</td>
                      <td>
                        <button className="btn btn-sm btn-outline" style={{ fontSize: 11 }}
                          onClick={() => { setEditTaxProfile(tp); setShowTaxEdit(true) }}>編集</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────── */}
      {/* TAB 3: 源泉所得税集計                  */}
      {/* ─────────────────────────────────────── */}
      {tab === 'withholding' && (
        <div>
          {withholding && (
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
              {[
                ['年間課税支給合計（給与）', `¥${fmt(withholding.year_total?.taxable_pay_total)}`],
                ['年間源泉所得税合計（給与+賞与）', `¥${fmt(withholding.year_total?.income_tax_total)}`],
                ['　内訳: 給与分', `¥${fmt(withholding.year_total?.income_tax_salary_total)}`],
                ['　内訳: 賞与分', `¥${fmt(withholding.year_total?.income_tax_bonus_total)}`],
                ['年間総支給合計（給与）', `¥${fmt(withholding.year_total?.gross_pay_total)}`],
                ['年間賞与総額', `¥${fmt(withholding.year_total?.bonus_gross_amount_total)}`],
              ].map(([label, val]) => (
                <div key={label} className="metric" style={{ padding: '10px 16px', minWidth: 180 }}>
                  <div className="metric-label">{label}</div>
                  <div className="metric-value" style={{ fontSize: 18 }}>{val}</div>
                </div>
              ))}
            </div>
          )}

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
              <span className="card-title">アシスタント別 年間源泉徴収サマリー — {year}年</span>
              <button className="btn btn-outline btn-sm" onClick={handleAssistantWithholdingCsv}
                disabled={!assistantWithholding || (assistantWithholding.assistants?.length ?? 0) === 0}>
                CSV出力
              </button>
            </div>
            <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--ink-3)', borderBottom: '1px solid var(--paper-2)' }}>
              ※ アシスタント本人画面には表示しません。管理者が源泉徴収票用データ・年末確認用として使う年間集計です。月次の細かい内訳は「月次給与一覧」を参照してください。
            </div>
            {assistantWithholding && (
              <div style={{ display: 'flex', gap: 12, padding: '12px 16px', flexWrap: 'wrap', borderBottom: '1px solid var(--paper-2)' }}>
                {[
                  ['対象者', `${assistantWithholding.totals?.assistant_count ?? 0}名`],
                  ['源泉徴収票対象', `${assistantWithholding.totals?.withholding_slip_target_count ?? 0}名`],
                  ['確認あり', `${assistantWithholding.totals?.warning_count ?? 0}名`],
                  ['年間源泉所得税（給与+賞与）', `¥${fmt(assistantWithholding.totals?.income_tax_total)}`],
                  ['年間支給額（給与+賞与）', `¥${fmt(assistantWithholding.totals?.annual_payment_total)}`],
                ].map(([label, val]) => (
                  <div key={label} className="metric" style={{ padding: '8px 12px', minWidth: 140 }}>
                    <div className="metric-label">{label}</div>
                    <div className="metric-value" style={{ fontSize: 16 }}>{val}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="table-wrap" style={{ overflowX: 'auto', width: '100%' }}>
              <table style={{ minWidth: 1420, tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: 120 }} />
                  <col style={{ width: 82 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 86 }} />
                  <col style={{ width: 92 }} />
                  <col style={{ width: 92 }} />
                  <col style={{ width: 108 }} />
                  <col style={{ width: 118 }} />
                  <col style={{ width: 118 }} />
                  <col style={{ width: 118 }} />
                  <col style={{ width: 118 }} />
                  <col style={{ width: 118 }} />
                  <col style={{ width: 118 }} />
                  <col style={{ width: 118 }} />
                  <col style={{ width: 220 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>氏名</th>
                    <th>税区分</th>
                    <th>扶養申告書</th>
                    <th style={{ textAlign: 'right' }}>支払回数</th>
                    <th>年末調整</th>
                    <th>源泉票</th>
                    <th style={{ textAlign: 'right' }}>課税支給</th>
                    <th style={{ textAlign: 'right' }}>非課税支給</th>
                    <th style={{ textAlign: 'right' }}>給与総支給額</th>
                    <th style={{ textAlign: 'right' }}>社保等</th>
                    <th style={{ textAlign: 'right' }}>源泉所得税(給与/賞与)</th>
                    <th style={{ textAlign: 'right' }}>賞与総額</th>
                    <th style={{ textAlign: 'right' }}>年間支給額</th>
                    <th style={{ textAlign: 'right' }}>差引支給(給与)</th>
                    <th>確認事項</th>
                  </tr>
                </thead>
                <tbody>
                  {!assistantWithholding || assistantWithholding.assistants?.length === 0 ? (
                    <tr><td colSpan={15} style={{ textAlign: 'center', padding: 32, color: 'var(--ink-3)' }}>データなし</td></tr>
                  ) : assistantWithholding.assistants.map((r: any) => {
                    const warnings = r.warnings ?? []
                    return (
                      <tr key={r.assistant_id} style={{ background: warnings.length > 0 ? 'rgba(176,122,18,0.04)' : undefined }}>
                        <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{r.assistant_name}</td>
                        <td><span className={`badge ${r.tax_column === 'ko' ? 'badge-blue' : r.tax_column ? 'badge-amber' : 'badge-gray'}`}>{r.tax_column ? (TAX_COL_LABELS[r.tax_column] ?? r.tax_column) : '未設定'}</span></td>
                        <td>{r.tax_profile_id ? (r.has_fuyou_form ? <span className="badge badge-green">提出済</span> : <span className="badge badge-red">未提出</span>) : <span className="badge badge-gray">未設定</span>}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>{r.payment_count ?? 0}回</td>
                        <td>{r.tax_profile_id ? (r.year_end_adjustment_enabled ? '対象' : <span style={{ color: 'var(--ink-3)' }}>対象外</span>) : '—'}</td>
                        <td>{r.tax_profile_id ? (r.withholding_slip_required ? '対象' : <span style={{ color: 'var(--ink-3)' }}>対象外</span>) : '—'}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(r.taxable_pay_total)}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(r.non_taxable_pay_total)}</td>
                        <td className="mono" style={{ textAlign: 'right', fontWeight: 600 }}>¥{fmt(r.gross_pay_total)}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(r.social_insurance_total)}</td>
                        <td className="mono" style={{ textAlign: 'right', fontWeight: 600 }}>
                          ¥{fmt(r.income_tax_total)}
                          <div style={{ fontSize: 10, color: 'var(--ink-3)', fontWeight: 400 }}>
                            ¥{fmt(r.income_tax_salary_total)} / ¥{fmt(r.income_tax_bonus_total)}
                          </div>
                        </td>
                        <td className="mono" style={{ textAlign: 'right' }}>
                          ¥{fmt(r.bonus_gross_amount_total)}
                          {Number(r.bonus_count ?? 0) > 0 && (
                            <div style={{ fontSize: 10, color: 'var(--ink-3)', fontWeight: 400 }}>{r.bonus_count}件</div>
                          )}
                        </td>
                        <td className="mono" style={{ textAlign: 'right', fontWeight: 600 }}>¥{fmt(r.annual_payment_total)}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(r.net_pay_total)}</td>
                        <td style={{ fontSize: 11, color: warnings.length > 0 ? 'var(--amber)' : 'var(--ink-3)', whiteSpace: 'normal' }}>
                          {warnings.length > 0 ? warnings.join(' / ') : '—'}
                        </td>
                      </tr>
                    )
                  })}
                  {assistantWithholding && assistantWithholding.assistants?.length > 0 && (
                    <tr style={{ background: 'var(--paper-2)', fontWeight: 700 }}>
                      <td>合計</td><td></td><td></td>
                      <td className="mono" style={{ textAlign: 'right' }}>{assistantWithholding.totals?.payment_count ?? 0}回</td>
                      <td></td><td></td>
                      <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(assistantWithholding.totals?.taxable_pay_total)}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(assistantWithholding.totals?.non_taxable_pay_total)}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(assistantWithholding.totals?.gross_pay_total)}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(assistantWithholding.totals?.social_insurance_total)}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(assistantWithholding.totals?.income_tax_total)}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(assistantWithholding.totals?.bonus_gross_amount_total)}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(assistantWithholding.totals?.annual_payment_total)}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(assistantWithholding.totals?.net_pay_total)}</td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">源泉所得税 月別集計 — {year}年</span></div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>対象月</th><th>支給人数</th>
                    <th style={{ textAlign: 'right' }}>課税支給額合計（給与）</th>
                    <th style={{ textAlign: 'right' }}>賞与総額</th>
                    <th style={{ textAlign: 'right' }}>源泉所得税合計（給与/賞与）</th>
                    <th>納付期限</th>
                    <th>備考</th>
                  </tr>
                </thead>
                <tbody>
                  {!withholding || withholding.months?.length === 0 ? (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--ink-3)' }}>データなし</td></tr>
                  ) : withholding.months?.map((r: any) => {
                    const isPast = r.due_date < new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10)
                    return (
                      <tr key={r.payroll_month}>
                        <td className="mono" style={{ fontWeight: 500 }}>{r.payroll_month}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>{r.headcount}名{Number(r.bonus_headcount ?? 0) > 0 ? ` (+賞与${r.bonus_headcount})` : ''}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(r.taxable_pay_total)}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(r.bonus_gross_amount_total)}</td>
                        <td className="mono" style={{ textAlign: 'right', fontWeight: 600 }}>
                          ¥{fmt(r.income_tax_total)}
                          <span style={{ fontSize: 10, color: 'var(--ink-3)', fontWeight: 400, marginLeft: 4 }}>
                            (¥{fmt(r.income_tax_salary_total)} / ¥{fmt(r.income_tax_bonus_total)})
                          </span>
                        </td>
                        <td className="mono" style={{ fontSize: 12, color: isPast ? 'var(--ink-3)' : 'var(--amber)' }}>
                          {r.due_date}
                        </td>
                        <td style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                          ※ 翌月10日納付（原則）
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--ink-3)', borderTop: '1px solid var(--paper-2)' }}>
              ※ 納付期限は原則として翌月10日です。納期の特例の適用を受けている場合は異なります。
              <br/>納付書PDFやe-Tax連携は将来実装予定です。
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────── */}
      {/* TAB 4: 統合月次一覧（通常アシスタント＋ヘルプスタッフ、読み取り専用）*/}
      {/* ─────────────────────────────────────── */}
      {tab === 'combined' && (
        <div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}>
            通常アシスタント（給与管理）とヘルプスタッフ（ヘルプスタッフ管理）の支払対象を、月次で一覧表示します。
            この一覧は読み取り専用です。編集は各管理画面（給与管理／ヘルプスタッフ管理）から行ってください。
          </div>

          {payeesLoading ? (
            <div className="loading" style={{ padding: 40 }}>LOADING...</div>
          ) : payees.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-3)' }}>
              {month} の支払対象がありません。
            </div>
          ) : (
            <div className="card" style={{ overflow: 'hidden' }}>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>区分</th>
                      <th>氏名</th>
                      <th style={{ textAlign: 'right' }}>金額</th>
                      <th>状態</th>
                      <th style={{ textAlign: 'right' }}>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payees.map((p) => (
                      <tr key={`${p.source_type}:${p.source_type === 'bonus' ? (p.details as any)?.bonus_record_id : p.source_id}`}>
                        <td>
                          <span className={`badge ${p.source_type === 'help' ? 'badge-blue' : p.source_type === 'bonus' ? 'badge-amber' : 'badge-gray'}`}>
                            {p.source_type === 'help' ? 'ヘルプスタッフ' : p.source_type === 'bonus' ? '賞与' : '通常アシスタント'}
                          </span>
                        </td>
                        <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                          {p.name}
                          {p.source_type === 'bonus' && (
                            <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 6 }}>
                              （{String((p.details as any)?.bonus_name ?? '')}）
                            </span>
                          )}
                        </td>
                        <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(p.amount)}</td>
                        <td>
                          <span className={`badge ${p.is_settled ? 'badge-green' : 'badge-gray'}`}>
                            {payeeStatusLabel(p)}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {p.source_type === 'help' && (
                            <button className="btn btn-sm btn-outline" style={{ fontSize: 11 }}
                              onClick={() => setHelpPreview({ id: p.source_id, name: p.name })}>
                              ヘルプ給与明細
                            </button>
                          )}
                          {p.source_type === 'bonus' && (
                            <button className="btn btn-sm btn-outline" style={{ fontSize: 11 }}
                              onClick={() => openBonusPreviewFromPayee(String((p.details as any)?.bonus_record_id ?? ''))}>
                              賞与明細
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ background: 'var(--paper-2)', fontWeight: 700 }}>
                      <td colSpan={2}>合計（{payees.length}件）</td>
                      <td className="mono" style={{ textAlign: 'right' }}>
                        ¥{fmt(payees.reduce((s, p) => s + p.amount, 0))}
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────── */}
      {/* TAB 5: 賞与一覧（全アシスタント横断）    */}
      {/* ─────────────────────────────────────── */}
      {tab === 'bonuses' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <button className="btn btn-outline btn-sm" onClick={() => {
              setEditBonusRecord({ assistant_id: '' } as any)
              setShowBonusEdit(true)
            }}>+ 賞与を追加</button>
          </div>

          {bonusLoading ? (
            <div className="loading" style={{ padding: 40 }}>LOADING...</div>
          ) : bonusRecords.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-3)' }}>
              {year}年の賞与はまだ登録されていません。
            </div>
          ) : (
            <div className="card" style={{ overflow: 'hidden' }}>
              <div className="table-wrap" style={{ overflowX: 'auto', width: '100%' }}>
                <table style={{ width: '100%', minWidth: 900 }}>
                  <thead>
                    <tr>
                      <th>賞与名</th>
                      <th>対象者</th>
                      <th>支給日</th>
                      <th style={{ textAlign: 'right' }}>総支給額</th>
                      <th style={{ textAlign: 'right' }}>所得税</th>
                      <th style={{ textAlign: 'right' }}>差引支給額</th>
                      <th>状態</th>
                      <th style={{ textAlign: 'right' }}>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bonusRecords.map((r: any) => (
                      <tr key={r.id} style={{ opacity: r.status === 'canceled' ? 0.45 : 1 }}>
                        <td style={{ fontWeight: 500 }}>{r.bonus_name}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{r.assistant_name}</td>
                        <td className="mono" style={{ fontSize: 12 }}>{r.payment_date}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(r.gross_amount)}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>{fmtOrDash(r.income_tax)}</td>
                        <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>¥{fmt(r.net_amount)}</td>
                        <td>
                          <span className={`badge ${STATUS_BADGE[r.status] ?? 'badge-gray'}`}
                            style={{ display: 'inline-block', minWidth: 64, textAlign: 'center' }}>
                            {BONUS_STATUS_LABELS[r.status] ?? r.status}
                          </span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                          <button className="btn btn-sm btn-ghost"
                            onClick={() => setPreviewBonusRecord(r)}
                            style={{ fontSize: 11 }}>プレビュー</button>
                          <button className="btn btn-sm btn-outline"
                            onClick={() => { setEditBonusRecord(r); setShowBonusEdit(true) }}
                            style={{ fontSize: 11, marginLeft: 4 }}>編集</button>
                          {r.status !== 'canceled' && (
                            <button className="btn btn-sm btn-ghost"
                              style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 4 }}
                              onClick={() => setConfirmBonusOp({ id: r.id, name: `${r.assistant_name} / ${r.bonus_name}` })}>
                              ✕
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ background: 'var(--paper-2)', fontWeight: 700 }}>
                      <td colSpan={3}>合計（{bonusRecords.filter((r: any) => r.status !== 'canceled').length}件）</td>
                      <td className="mono" style={{ textAlign: 'right' }}>
                        ¥{fmt(bonusRecords.filter((r: any) => r.status !== 'canceled').reduce((s: number, r: any) => s + r.gross_amount, 0))}
                      </td>
                      <td className="mono" style={{ textAlign: 'right' }}>
                        ¥{fmt(bonusRecords.filter((r: any) => r.status !== 'canceled').reduce((s: number, r: any) => s + r.income_tax, 0))}
                      </td>
                      <td className="mono" style={{ textAlign: 'right' }}>
                        ¥{fmt(bonusRecords.filter((r: any) => r.status !== 'canceled').reduce((s: number, r: any) => s + r.net_amount, 0))}
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}